import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { MediaItem } from '../App';

interface MediaViewerProps {
  media: MediaItem;
  onClose: () => void;
  mediaItems: MediaItem[]; 
  onNavigate: (mediaId: string) => void;
  slideshowActive?: boolean;
  onToggleSlideshow?: () => void;
  onDelete?: (mediaId: string) => Promise<boolean>;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ 
  media, 
  onClose, 
  mediaItems,
  onNavigate,
  slideshowActive = false,
  onToggleSlideshow,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  // Reset loading state when media changes
  useEffect(() => {
    if (media.content_type === 'image') {
      setIsImageLoading(true);
    }
  }, [media.id, media.content_type]);

  useEffect(() => {
    // Close viewer or delete dialog when ESC key is pressed
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Close delete confirmation dialog if open, otherwise close viewer
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft' && !showDeleteConfirm) {
        navigateToPrevious();
      } else if (e.key === 'ArrowRight' && !showDeleteConfirm) {
        navigateToNext();
      } else if (e.key === 'Space' && !showDeleteConfirm) {
        e.preventDefault(); // Prevent page scroll
        onToggleSlideshow?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Prevent body scrolling while viewer is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [media.id, onClose, onToggleSlideshow, showDeleteConfirm]); // Re-add event listener when media or dialog state changes

  // Find current index
  const currentIndex = mediaItems.findIndex(item => item.id === media.id);
  
  // Navigate to previous media
  const navigateToPrevious = () => {
    if (currentIndex > 0) {
      const previousItem = mediaItems[currentIndex - 1];
      onNavigate(previousItem.id);
    }
  };
  
  // Navigate to next media
  const navigateToNext = () => {
    if (currentIndex < mediaItems.length - 1) {
      const nextItem = mediaItems[currentIndex + 1];
      onNavigate(nextItem.id);
    }
  };

  // Determine if we have previous/next items
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < mediaItems.length - 1;

  // Handle download - using fetch to force download instead of opening in browser
  const handleDownload = async () => {
    try {
      // Fetch the file as a blob
      const response = await fetch(media.url);
      const blob = await response.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Extract filename from URL or use media name
      const filename = media.name || media.url.split('/').pop() || 'download';
      
      // Create anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  // Handle delete media
  const handleDeleteClick = () => {
    console.log('Delete button clicked, showing confirmation dialog');
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        const success = await onDelete(media.id);
        if (!success) {
          alert('Failed to delete media. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting media:', error);
        alert('Failed to delete media. Please try again.');
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={() => {
        // Only close viewer if delete dialog is not shown
        if (!showDeleteConfirm) {
          onClose();
        }
      }}
    >
      {/* Delete confirmation dialog using createPortal to render directly to body */}
      {showDeleteConfirm && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
          onClick={handleCancelDelete}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete this {media.content_type}? This action cannot be undone.
            </p>
            <p className="text-gray-700 mb-4 text-sm italic">
              Note: You should only delete media that you uploaded yourself.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 rounded-md text-white hover:bg-red-700 focus:outline-none flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div 
        className="relative max-w-5xl max-h-[90vh] p-4 w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - moved from negative position to avoid being cut off on small screens */}
        <button 
          className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white text-gray-800 flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors z-10"
          onClick={onClose}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Slideshow indicator */}
        {slideshowActive && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-800">
            <div className="h-full bg-blue-500 animate-[slideshow-progress_5s_linear_infinite]"></div>
          </div>
        )}

        {/* Media content */}
        <div className="relative bg-black rounded-lg overflow-hidden">
          {media.content_type === 'video' ? (
            <video 
              src={media.url} 
              controls 
              autoPlay 
              className="max-w-full max-h-[70vh] sm:max-h-[80vh] mx-auto"
            />
          ) : (
            <div className="relative flex items-center justify-center min-h-[50vh]">
              {isImageLoading && (
                <div className="absolute z-10 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 border-4 border-t-blue-500 border-r-transparent border-b-blue-500 border-l-transparent rounded-full animate-spin mb-2"></div>
                  <p className="text-white text-sm">Loading high-resolution image...</p>
                </div>
              )}
              <img
                src={media.url}
                alt={media.name}
                className={`max-w-full max-h-[70vh] sm:max-h-[80vh] mx-auto transition-opacity duration-300 ${isImageLoading ? 'opacity-30' : 'opacity-100'}`}
                onLoad={() => setIsImageLoading(false)}
                onError={(e) => {
                  console.error("Error loading image in viewer:", media.url);
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600?text=Image+Error';
                  setIsImageLoading(false);
                }}
              />
            </div>
          )}
          
          {/* Navigation arrows */}
          <div className="absolute inset-y-0 left-0 flex items-center">
            {hasPrevious && !slideshowActive && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToPrevious();
                }}
                className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 sm:p-2 rounded-r-lg ml-1 sm:ml-2 focus:outline-none transition-colors"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>
            )}
          </div>
          
          <div className="absolute inset-y-0 right-0 flex items-center">
            {hasNext && !slideshowActive && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToNext();
                }}
                className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 sm:p-2 rounded-l-lg mr-1 sm:mr-2 focus:outline-none transition-colors"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Media info, download button, and navigation counter */}
        <div className="mt-4 text-center text-white flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
          <div className="w-full sm:flex-1 text-center sm:text-left">
            <div className="text-sm text-gray-300">
              {currentIndex + 1} of {mediaItems.length}
            </div>
          </div>
          
          <div className="w-full sm:flex-1 flex flex-wrap items-center justify-center sm:justify-end gap-2">
            {/* Delete button */}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                disabled={isDeleting}
                className="px-3 py-1 rounded text-sm font-medium bg-red-500 hover:bg-red-600 transition-colors flex items-center gap-1"
                aria-label="Delete media"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                Delete
              </button>
            )}

            {/* Slideshow button */}
            {onToggleSlideshow && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSlideshow();
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  slideshowActive 
                    ? "bg-red-500 hover:bg-red-600" 
                    : "bg-blue-500 hover:bg-blue-600"
                } transition-colors`}
              >
                {slideshowActive ? "Stop" : "Slideshow"}
              </button>
            )}

            {/* Download button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
              className="px-3 py-1 rounded text-sm font-medium bg-green-500 hover:bg-green-600 transition-colors flex items-center gap-1"
              aria-label="Download media"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaViewer;